import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Tooltip, WarningIcon, cn } from '@playerbase/ui';
import type { GameSentimentSummaryRow } from '@playerbase/types';

import { fetchGameSentimentSummary } from '../games/feedback-forms-data.js';
import { fetchGameThemes } from '../insights/insights-data.js';

// ---------------------------------------------------------------------------
// Community Feedback summary.
//
// Sits above the Feedback stream. Reads the game's scored-sentiment counts
// (per source) and active patterns once on mount, then renders a headline
// signal count, the positive/neutral/negative split, and a game-wide patterns
// card that links into Insights. The source chips (All / Channels / Polls /
// Forms) recompute the cards locally - they do NOT touch the stream below.
//
// Copy stays in the product's plain vocabulary (Positive / Neutral /
// Negative, Community feedback, Patterns) - the scoring layer is never named.
// ---------------------------------------------------------------------------

type SourceFilter = 'all' | 'channels' | 'polls' | 'forms';

const SOURCE_SETS: Record<SourceFilter, GameSentimentSummaryRow['source'][]> = {
  all: ['discord_channel', 'form', 'poll'],
  channels: ['discord_channel'],
  polls: ['poll'],
  forms: ['form'],
};

const SOURCE_CHIPS: { id: SourceFilter; label: string }[] = [
  { id: 'all', label: 'All feedback' },
  { id: 'channels', label: 'Channels' },
  { id: 'polls', label: 'Polls' },
  { id: 'forms', label: 'Forms' },
];

const SIGNAL_TOOLTIP =
  'Messages, polls or form submissions that carried some game feedback related signal.';

interface CommunityFeedbackSummaryProps {
  gameId: string;
  studioShortId: string;
  gameShortId: string;
}

interface Totals {
  scored: number;
  positive: number;
  neutral: number;
  negative: number;
  thisWeek: number;
}

function sumRows(
  rows: GameSentimentSummaryRow[],
  sources: GameSentimentSummaryRow['source'][],
): Totals {
  const t: Totals = { scored: 0, positive: 0, neutral: 0, negative: 0, thisWeek: 0 };
  for (const r of rows) {
    if (!sources.includes(r.source)) continue;
    t.scored += r.scoredCount;
    t.positive += r.positiveCount;
    t.neutral += r.neutralCount;
    t.negative += r.negativeCount;
    t.thisWeek += r.scoredThisWeek;
  }
  return t;
}

function pct(count: number, total: number): number {
  return total > 0 ? Math.round((count / total) * 100) : 0;
}

export function CommunityFeedbackSummary({
  gameId,
  studioShortId,
  gameShortId,
}: CommunityFeedbackSummaryProps): JSX.Element {
  const [rows, setRows] = useState<GameSentimentSummaryRow[]>([]);
  const [patternsTotal, setPatternsTotal] = useState(0);
  const [criticalCount, setCriticalCount] = useState(0);
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [summary, themes] = await Promise.all([
        fetchGameSentimentSummary(gameId),
        fetchGameThemes(gameId),
      ]);
      if (cancelled) return;
      setRows(summary);
      // Patterns are game-wide; "none" themes are the Simmering bucket and
      // don't count as a detected pattern.
      setPatternsTotal(themes.filter((t) => t.bucket !== 'none').length);
      setCriticalCount(themes.filter((t) => t.bucket === 'critical').length);
    })();
    return () => {
      cancelled = true;
    };
  }, [gameId]);

  const totals = useMemo(
    () => sumRows(rows, SOURCE_SETS[sourceFilter]),
    [rows, sourceFilter],
  );

  return (
    <section className="flex flex-col gap-4 rounded-lg border border-border bg-surface-2 p-4 sm:p-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-col gap-1">
          <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-text-muted">
            Community feedback
          </span>
          <h2 className="font-display text-lg font-bold text-text">
            {SOURCE_CHIPS.find((c) => c.id === sourceFilter)?.label ?? 'All feedback'}
          </h2>
        </div>
        <div
          role="tablist"
          aria-label="Filter summary by source"
          className="flex flex-wrap gap-1 self-start rounded-md border border-border bg-surface p-1"
        >
          {SOURCE_CHIPS.map((c) => (
            <button
              key={c.id}
              type="button"
              role="tab"
              aria-selected={sourceFilter === c.id}
              onClick={() => setSourceFilter(c.id)}
              className={cn(
                'rounded px-2.5 py-1 font-mono text-xs font-medium transition-colors hover:bg-surface-3',
                sourceFilter === c.id ? 'bg-surface-3 text-text shadow-sm' : 'text-text-muted',
              )}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {/* Signals */}
        <div className="rounded-md border border-border bg-surface p-3">
          <div className="font-display text-2xl font-bold text-text">
            {totals.scored.toLocaleString()}
          </div>
          <div className="mt-1 flex items-center gap-1 text-xs text-text-muted">
            <span>Feedback signals</span>
            <Tooltip content={SIGNAL_TOOLTIP} multiline>
              <span
                aria-label="What counts as a feedback signal"
                className="inline-flex h-3.5 w-3.5 cursor-help items-center justify-center rounded-full border border-text-muted text-[9px] font-bold leading-none text-text-muted"
              >
                i
              </span>
            </Tooltip>
          </div>
          {totals.thisWeek > 0 && (
            <div className="mt-0.5 text-xs text-teal-text">
              ↑ {totals.thisWeek.toLocaleString()} this week
            </div>
          )}
        </div>

        <SentimentStat band="positive" count={totals.positive} percentage={pct(totals.positive, totals.scored)} caption="4-5 / 5" />
        <SentimentStat band="neutral" count={totals.neutral} percentage={pct(totals.neutral, totals.scored)} caption="3 / 5" />
        <SentimentStat band="negative" count={totals.negative} percentage={pct(totals.negative, totals.scored)} caption="1-2 / 5" />

        {/* Patterns is game-wide (not source-filterable) and links into
            Insights, so it sits in a distinct dashed tone to read apart from
            the source-filterable cards. */}
        <Link
          to={`/s/${studioShortId}/g/${gameShortId}/insights`}
          className="group rounded-md border border-dashed border-border bg-surface p-3 transition-colors hover:border-accent hover:bg-surface-3"
        >
          <div className="flex items-start justify-between gap-2">
            <span className="font-display text-2xl font-bold text-text">{patternsTotal}</span>
            {criticalCount > 0 && (
              <WarningIcon className="text-warning-text" />
            )}
          </div>
          <div className="mt-1 text-xs text-text-muted group-hover:text-text">
            Patterns detected
          </div>
          {criticalCount > 0 && (
            <div className="mt-0.5 text-xs text-danger-text">{criticalCount} critical</div>
          )}
        </Link>
      </div>
    </section>
  );
}

const BAND_LABEL = {
  positive: 'Positive',
  neutral: 'Neutral',
  negative: 'Negative',
} as const;

const BAND_NUMBER_CLASS = {
  positive: 'text-teal-text',
  neutral: 'text-text',
  negative: 'text-danger-text',
} as const;

interface SentimentStatProps {
  band: keyof typeof BAND_LABEL;
  count: number;
  percentage: number;
  caption: string;
}

// Display-only band breakdown. (Filtering the feed by sentiment is a possible
// future feature; for now these are read-only stats.)
function SentimentStat({ band, count, percentage, caption }: SentimentStatProps): JSX.Element {
  return (
    <div className="rounded-md border border-border bg-surface p-3">
      <div className={cn('font-display text-2xl font-bold', BAND_NUMBER_CLASS[band])}>
        {count.toLocaleString()}
      </div>
      <div className="mt-1 text-xs text-text-muted">
        {BAND_LABEL[band]} <span className="tabular-nums">{percentage}%</span>
      </div>
      <div className="mt-0.5 text-xs text-text-muted">{caption}</div>
    </div>
  );
}
